/**
 * Canonical shape stored in `listings.shipping_config` (JSON column).
 *
 * Historical context:
 *   - `components/publications/creation-flow/listing-creation/steps/review-step.tsx`
 *     already builds this array shape via its local `buildShippingConfig`.
 *   - The TCG creation flow (`card-review-step.tsx`) used to dump the raw
 *     wizard draft (`{ methods, customOptions }`) directly into the column,
 *     which the cart pipeline treated as empty (no `shipping_method_id`,
 *     wrong casing on `combined_fee` / `allow_combined`). Every downstream
 *     reader (`parseShippingConfig`, `/api/cart`, checkout hooks) returned
 *     `[]` for that object, so TCG listings had no shipping methods and the
 *     buy-now flow silently dropped items before reaching checkout.
 *
 * Keep this module the single source of truth for shipping-config
 * serialization and parsing.
 */

export type CanonicalShippingConfigEntry = {
  shipping_method_id: string;
  label: string;
  price: number | string;
  allow_combined: boolean;
  combined_fee: number | string;
};

type WizardDraftOption = {
  id?: string;
  shipping_method_id?: string;
  label?: string;
  name?: string;
  price?: number | string;
  allowCombined?: boolean;
  allow_combined?: boolean;
  combinedFee?: number | string;
  combined_fee?: number | string;
};

type WizardDraftShape = {
  methods?: string[];
  customOptions?: WizardDraftOption[];
};

export function normalizeShippingConfig(raw: unknown): CanonicalShippingConfigEntry[] {
  if (raw == null) return [];

  if (typeof raw === "string") {
    try {
      return normalizeShippingConfig(JSON.parse(raw));
    } catch {
      return [];
    }
  }

  if (Array.isArray(raw)) {
    return raw
      .map(normalizeEntry)
      .filter((entry): entry is CanonicalShippingConfigEntry => entry !== null);
  }

  if (typeof raw === "object") {
    const draft = raw as WizardDraftShape;
    const selected = Array.isArray(draft.methods) ? draft.methods : [];
    const options = Array.isArray(draft.customOptions) ? draft.customOptions : [];
    const filter = selected.length > 0 ? new Set(selected) : null;

    const resolved = options
      .filter((option) => {
        const key = String(option?.id ?? option?.shipping_method_id ?? "");
        if (!key) return false;
        return filter ? filter.has(key) : true;
      })
      .map(normalizeEntry)
      .filter((entry): entry is CanonicalShippingConfigEntry => entry !== null);

    return resolved;
  }

  return [];
}

function normalizeEntry(entry: unknown): CanonicalShippingConfigEntry | null {
  if (!entry || typeof entry !== "object") return null;
  const source = entry as WizardDraftOption;

  const shippingMethodId = String(source.shipping_method_id ?? source.id ?? "").trim();
  if (!shippingMethodId || shippingMethodId === "undefined" || shippingMethodId === "null") {
    return null;
  }

  const label = typeof source.label === "string" && source.label.trim().length > 0
    ? source.label
    : typeof source.name === "string"
      ? source.name
      : "";

  const price = source.price ?? 0;
  const allowCombined = Boolean(source.allow_combined ?? source.allowCombined ?? false);
  const combinedFee = source.combined_fee ?? source.combinedFee ?? 0;

  return {
    shipping_method_id: shippingMethodId,
    label,
    price,
    allow_combined: allowCombined,
    combined_fee: combinedFee,
  };
}
